import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { RootState } from ".";
import { logout } from "./slices/authSlice";

// API Response types
interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

type OrdersResponse = any[] | PaginatedResponse<any>;

// Создаем базовый запрос с обработкой ошибок
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || "http://localhost:3001/api",
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }

    // Не устанавливаем Content-Type для FormData (файлы)
    if (endpoint !== "uploadOrderReceipt" && endpoint !== "uploadAppealFiles") {
      headers.set("Content-Type", "application/json");
    }
    headers.set("Accept", "application/json");

    const headerSize = JSON.stringify(
      Object.fromEntries(headers.entries())
    ).length;

    if (headerSize > 16384) {
      console.warn(
        `⚠️ Large headers detected in provider: ${headerSize} bytes - token size: ${token?.length || 0}`
      );

      if (headerSize > 32768) {
        console.error(
          `❌ Headers too large in provider: ${headerSize} bytes - clearing storage`
        );
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
      }
    }

    return headers;
  },
  validateStatus: (response) => {
    if (response.status === 431) {
      console.error("❌ Error 431: Request Header Fields Too Large");
      console.log("🧹 Clearing all storage due to 431 error...");
      localStorage.clear();
      sessionStorage.clear();

      setTimeout(() => {
        window.location.href = "/login";
      }, 100);
      return false;
    }

    return response.status >= 200 && response.status < 300;
  },
  fetchFn: async (...args) => {
    try {
      return await fetch(...args);
    } catch (error) {
      console.error("❌ Provider Fetch error:", error);
      throw error;
    }
  },
});

// Добавляем обработку 401 ошибок
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    console.log("401 Unauthorized - logging out");
    api.dispatch(logout());
    window.location.href = "/login";
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "User",
    "Order",
    "Balance",
    "Wallet",
    "Bank",
    "Settings",
    "Provider",
    "Analytics",
    "Appeals",
    "Withdrawals",
    "ExchangeRate",
  ],
  endpoints: (builder) => ({
    login: builder.mutation<
      { user: any; token: string; refreshToken: string },
      { login: string; password: string }
    >({
      query: (credentials) => ({
        url: "/auth/login?requiredRole=PROVIDER",
        method: "POST",
        body: credentials,
      }),
    }),

    refreshToken: builder.mutation<
      { token: string; refreshToken: string },
      { refreshToken: string }
    >({
      query: (body) => ({
        url: "/auth/refresh",
        method: "POST",
        body,
      }),
    }),

    getProfile: builder.query<any, void>({
      query: () => "/auth/profile",
      providesTags: ["User"],
    }),

    getOrders: builder.query<
      OrdersResponse,
      {
        page?: number;
        limit?: number;
        status?: string;
        search?: string;
        bank?: string;
        method?: string;
        amountMin?: number;
        amountMax?: number;
        usdtMin?: number;
        usdtMax?: number;
        dateFrom?: string;
        dateTo?: string;
      }
    >({
      query: (params) => ({
        url: "/orders",
        params,
      }),
      providesTags: ["Order"],
    }),

    updateOrderStatus: builder.mutation<any, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/orders/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Order"],
    }),

    getBalance: builder.query<any, void>({
      query: () => "/balance",
      providesTags: ["Balance"],
    }),

    getWallets: builder.query<any, { page?: number; limit?: number }>({
      query: (params = {}) => ({
        url: "/wallets",
        params,
      }),
      providesTags: ["Wallet"],
    }),

    createWallet: builder.mutation<
      any,
      { address: string; privateKey: string; name?: string }
    >({
      query: (body) => ({
        url: "/wallets",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Wallet"],
    }),

    getWallet: builder.query<any, string>({
      query: (id) => `/wallets/${id}`,
      providesTags: ["Wallet"],
    }),

    updateWallet: builder.mutation<
      any,
      { id: string; data: { name?: string; isActive?: boolean } }
    >({
      query: ({ id, data }) => ({
        url: `/wallets/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Wallet"],
    }),

    deleteWallet: builder.mutation<any, string>({
      query: (id) => ({
        url: `/wallets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Wallet"],
    }),

    toggleWalletStatus: builder.mutation<any, string>({
      query: (id) => ({
        url: `/wallets/${id}/toggle-status`,
        method: "PATCH",
      }),
      invalidatesTags: ["Wallet"],
    }),

    syncWalletBalance: builder.mutation<any, string>({
      query: (id) => ({
        url: `/wallets/${id}/sync-balance`,
        method: "POST",
      }),
      invalidatesTags: ["Wallet"],
    }),

    getWalletStats: builder.query<any, void>({
      query: () => "/wallets/stats/overview",
      providesTags: ["Wallet"],
    }),

    getBanks: builder.query<any[], void>({
      query: () => "/banks",
      providesTags: ["Bank"],
    }),

    updateBank: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/banks/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Bank"],
    }),

    getSettings: builder.query<any, void>({
      query: () => "/settings",
      providesTags: ["Settings"],
    }),

    updateSettings: builder.mutation<any, any>({
      query: (data) => ({
        url: "/settings",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Settings"],
    }),

    // Провайдер эндпоинты
    getProviderStats: builder.query<any, string>({
      query: (providerId) => `/providers/${providerId}/stats`,
      providesTags: ["Provider"],
    }),

    getProviderInfo: builder.query<any, string>({
      query: (providerId) => `/providers/${providerId}`,
      providesTags: ["Provider"],
    }),

    getDashboardStats: builder.query<
      any,
      { period?: string; dateFrom?: string; dateTo?: string }
    >({
      query: (params) => ({
        url: "/analytics/dashboard",
        params,
      }),
      providesTags: ["Analytics"],
    }),

    getRevenueStats: builder.query<any, { period?: string; groupBy?: string }>({
      query: (params) => ({
        url: "/analytics/revenue",
        params,
      }),
      providesTags: ["Analytics"],
    }),

    getBalanceHistory: builder.query<
      any,
      { page?: number; limit?: number; type?: string }
    >({
      query: (params) => ({
        url: "/balance/history",
        params,
      }),
      providesTags: ["Balance"],
    }),

    // Провайдер-агностичные эндпоинты интеграции
    getProviderBalance: builder.query<any, void>({
      query: () => "/integration/balance",
      providesTags: ["Balance"],
    }),

    getProviderBanks: builder.query<any, void>({
      query: () => "/integration/banks",
      providesTags: ["Bank"],
    }),

    getAvailableBanks: builder.query<
      any,
      { sum: number; operation?: string; currency?: string }
    >({
      query: (params) => ({
        url: "/integration/banks/available",
        params,
      }),
      providesTags: ["Bank"],
    }),

    createProviderOrder: builder.mutation<any, any>({
      query: (data) => ({
        url: "/integration/orders",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Order"],
    }),

    checkOrderStatus: builder.query<any, string>({
      query: (invId) => `/integration/orders/${invId}/status`,
      providesTags: ["Order"],
    }),

    updateOrderToProcessing: builder.mutation<any, string>({
      query: (invId) => ({
        url: `/integration/orders/${invId}/processing`,
        method: "PUT",
      }),
      invalidatesTags: ["Order"],
    }),

    cancelOrder: builder.mutation<any, string>({
      query: (invId) => ({
        url: `/integration/orders/${invId}/cancel`,
        method: "PUT",
      }),
      invalidatesTags: ["Order"],
    }),

    // Appeals endpoints
    getAppeals: builder.query<
      PaginatedResponse<any>,
      { page?: number; limit?: number; status?: string; orderId?: string }
    >({
      query: ({ page = 1, limit = 10, status, orderId }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        if (status) params.append("status", status);
        if (orderId) params.append("orderId", orderId);

        return `appeals?${params}`;
      },
      providesTags: ["Appeals"],
    }),

    getAppeal: builder.query<any, string>({
      query: (id) => `appeals/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Appeals", id }],
    }),

    // Withdrawals endpoints
    createWithdrawal: builder.mutation<
      any,
      { amount: number; currency: string; walletAddress: string }
    >({
      query: (data) => ({
        url: "/auth/provider-withdrawal",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Withdrawals", "Balance"],
    }),

    getWithdrawals: builder.query<
      any,
      { page?: number; limit?: number; status?: string }
    >({
      query: (params) => ({
        url: "/withdrawals",
        params,
      }),
      providesTags: ["Withdrawals"],
    }),

    createAppeal: builder.mutation<any, any>({
      query: (data) => ({
        url: "/appeals",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Appeals"],
    }),

    uploadAppealFiles: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: "/appeals/upload",
        method: "POST",
        body: formData,
      }),
    }),

    uploadOrderReceipt: builder.mutation<
      any,
      { orderId: string; formData: FormData }
    >({
      query: ({ orderId, formData }) => ({
        url: `/orders/${orderId}/receipt`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "Order", id: orderId },
      ],
    }),

    updateAppealStatus: builder.mutation<any, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/appeals/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Appeals"],
    }),

    // Sessions endpoints
    getActiveSessions: builder.query<any[], void>({
      query: () => "/auth/sessions/active",
      providesTags: ["User"],
    }),

    // Exchange rates endpoints
    getExchangeRate: builder.query<
      {
        source: string;
        pair: string;
        rate: number;
        markup: string;
        timestamp: string;
      },
      void
    >({
      query: () => "/exchange-rates/rapira/usdt-rub",
      providesTags: ["ExchangeRate"],
      keepUnusedDataFor: 0, // Не кэшируем данные
    }),
    getOrderById: builder.query<any, string>({
      query: (id) => `/orders/${id}`,
      providesTags: (result, error, id) => [{ type: "Order", id }],
    }),

    terminateSession: builder.mutation<any, string>({
      query: (sessionId) => ({
        url: `/auth/sessions/${sessionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),

    logoutAllOtherSessions: builder.mutation<any, void>({
      query: () => ({
        url: "/auth/sessions/logout-all",
        method: "POST",
      }),
      invalidatesTags: ["User"],
    }),

    getProviderTariffs: builder.query<any, string>({
      query: (providerId) => `/providers/${providerId}/tariffs`,
      providesTags: ["Provider"],
    }),

    createProviderTariff: builder.mutation<
      any,
      { providerId: string; tariffData: any }
    >({
      query: ({ providerId, tariffData }) => ({
        url: `/providers/${providerId}/tariffs`,
        method: "POST",
        body: tariffData,
      }),
      invalidatesTags: ["Provider"],
    }),

    updateProviderTariff: builder.mutation<
      any,
      { providerId: string; tariffId: string; tariffData: any }
    >({
      query: ({ providerId, tariffId, tariffData }) => ({
        url: `/providers/${providerId}/tariffs/${tariffId}`,
        method: "PUT",
        body: tariffData,
      }),
      invalidatesTags: ["Provider"],
    }),

    deleteProviderTariff: builder.mutation<
      any,
      { providerId: string; tariffId: string }
    >({
      query: ({ providerId, tariffId }) => ({
        url: `/providers/${providerId}/tariffs/${tariffId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Provider"],
    }),

    getProviderLimits: builder.query<any, string>({
      query: (providerId) => `/providers/${providerId}/limits`,
      providesTags: ["Provider"],
    }),

    createProviderLimit: builder.mutation<
      any,
      { providerId: string; limitData: any }
    >({
      query: ({ providerId, limitData }) => ({
        url: `/providers/${providerId}/limits`,
        method: "POST",
        body: limitData,
      }),
      invalidatesTags: ["Provider"],
    }),

    updateProviderLimit: builder.mutation<
      any,
      { providerId: string; limitId: string; limitData: any }
    >({
      query: ({ providerId, limitId, limitData }) => ({
        url: `/providers/${providerId}/limits/${limitId}`,
        method: "PUT",
        body: limitData,
      }),
      invalidatesTags: ["Provider"],
    }),

    deleteProviderLimit: builder.mutation<
      any,
      { providerId: string; limitId: string }
    >({
      query: ({ providerId, limitId }) => ({
        url: `/providers/${providerId}/limits/${limitId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Provider"],
    }),

    exportData: builder.mutation<
      any,
      {
        exportType: string;
        format?: string;
        dateFrom?: string;
        dateTo?: string;
        status?: string;
        bank?: string;
      }
    >({
      query: (exportParams) => ({
        url: "/providers/export",
        method: "POST",
        body: exportParams,
      }),
    }),

    // Toggle online status
    toggleOnlineStatus: builder.mutation<
      { message: string; isActive: boolean; status: string },
      void
    >({
      query: () => ({
        url: "/auth/toggle-provider-status",
        method: "PATCH",
      }),
      invalidatesTags: ["User"],
    }),

    // Transit withdrawal requests
    createTransitWithdrawal: builder.mutation<
      {
        message: string;
        withdrawalId: string;
        amount: number;
        currency: string;
      },
      {
        amount: number;
        currency: string;
        walletAddress: string;
        comment?: string;
      }
    >({
      query: (data) => ({
        url: "/transit-withdrawals",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Balance"],
    }),

    getTransitWithdrawalAvailability: builder.query<
      {
        isAvailable: boolean;
        daysLeft: number;
        lastDepositDate: string | null;
        transitBalance: number;
      },
      void
    >({
      query: () => "/transit-withdrawals/availability",
      providesTags: ["Balance"],
    }),

    getTransitWithdrawals: builder.query<any[], void>({
      query: () => "/transit-withdrawals",
      providesTags: ["Balance"],
    }),

    // Get provider withdrawals (both working and transit)
    getProviderWithdrawals: builder.query<
      { data: Array<{
        id: string;
        amount: number;
        currency: string;
        status: string;
        balanceType: string;
        walletAddress: string;
        comment?: string;
        adminComment?: string;
        txHash?: string;
        createdAt: string;
        processedAt?: string;
        completedAt?: string;
      }>; total: number },
      void
    >({
      query: () => "/auth/provider-withdrawals",
      providesTags: ["Balance"],
    }),
  }),
});

export const {
  useLoginMutation,
  useRefreshTokenMutation,
  useGetProfileQuery,
  useGetOrdersQuery,
  useGetOrderByIdQuery,
  useUpdateOrderStatusMutation,
  useGetBalanceQuery,
  useGetWalletsQuery,
  useCreateWalletMutation,
  useGetWalletQuery,
  useUpdateWalletMutation,
  useDeleteWalletMutation,
  useToggleWalletStatusMutation,
  useSyncWalletBalanceMutation,
  useGetWalletStatsQuery,
  useGetBanksQuery,
  useUpdateBankMutation,
  useGetSettingsQuery,
  useUpdateSettingsMutation,

  // Новые хуки для провайдеров
  useGetProviderStatsQuery,
  useGetProviderInfoQuery,
  useGetDashboardStatsQuery,
  useGetRevenueStatsQuery,
  useGetBalanceHistoryQuery,
  useGetProviderBalanceQuery,
  useGetProviderBanksQuery,
  useGetAvailableBanksQuery,
  useCreateProviderOrderMutation,
  useCheckOrderStatusQuery,
  useUpdateOrderToProcessingMutation,
  useCancelOrderMutation,

  // Appeals hooks
  useGetAppealsQuery,
  useGetAppealQuery,
  useCreateAppealMutation,
  useUploadAppealFilesMutation,
  useUpdateAppealStatusMutation,

  // Order receipt hooks
  useUploadOrderReceiptMutation,

  // Withdrawals hooks
  useCreateWithdrawalMutation,
  useGetWithdrawalsQuery,

      // Transit withdrawals hooks
    useCreateTransitWithdrawalMutation,
    useGetTransitWithdrawalAvailabilityQuery,
    useGetTransitWithdrawalsQuery,

    // Provider withdrawals history
    useGetProviderWithdrawalsQuery,

  // Sessions hooks
  useGetActiveSessionsQuery,
  useTerminateSessionMutation,
  useLogoutAllOtherSessionsMutation,

  // Exchange rate hooks
  useGetExchangeRateQuery,

  // Provider tariffs hooks
  useGetProviderTariffsQuery,
  useCreateProviderTariffMutation,
  useUpdateProviderTariffMutation,
  useDeleteProviderTariffMutation,

  // Provider limits hooks
  useGetProviderLimitsQuery,
  useCreateProviderLimitMutation,
  useUpdateProviderLimitMutation,
  useDeleteProviderLimitMutation,

  // Export data hooks
  useExportDataMutation,

  // Online status hooks
  useToggleOnlineStatusMutation,
} = api;
